/**
 * Route container for the guided listing setup (US-109, BR-107).
 *   /leasing/listings/new/:unitId/:step   any step; before a Listing exists only marketing and details
 *                                         have a form, the rest explain that listing details come first
 *   /leasing/listings/:id/setup/:step     every step, once the draft Listing exists
 * The bare forms (/leasing/listings/new/:unitId, /leasing/listings/:id/setup) resume at the first
 * incomplete step (GuideResumePage). "Save and continue" calls the step's handle.save() and
 * advances only when it resolved true; the draft Listing is created by the Listing details step.
 */

import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '../../../components/ui/button';
import { Skeleton } from '../../../components/ui/skeleton';
import { closeListing, fetchListing, fetchUnits, type Listing } from '../../../lib/leasing.api';
import { GuideShell } from './GuideShell';
import {
  GUIDE_STEPS,
  STEPS_BEFORE_LISTING,
  guideStepLabel,
  isGuideStepKey,
  type GuideStepHandle,
  type GuideStepKey,
} from './guide-steps';
import { useGuideState } from './useGuideState';
import { AgentStep } from './steps/AgentStep';
import { ContactScreeningStep } from './steps/ContactScreeningStep';
import { ListingDetailsStep } from './steps/ListingDetailsStep';
import { MarketingStep } from './steps/MarketingStep';
import { ReviewStep } from './steps/ReviewStep';
import { ShowingsStep } from './steps/ShowingsStep';
import { formatUnitLabel } from '@resida/shared';

function guidePathFor(key: GuideStepKey, ids: { listingId?: string; unitId: string }): string {
  if (ids.listingId) {
    return key === 'preview'
      ? `/leasing/listings/${ids.listingId}/preview`
      : `/leasing/listings/${ids.listingId}/setup/${key}`;
  }
  return `/leasing/listings/new/${ids.unitId}/${key}`;
}

function GuideSkeleton() {
  return (
    <div data-testid="listing-guide-loading" className="mx-auto w-full max-w-[1280px] px-[var(--page-padding-x)] pt-6">
      <Skeleton className="h-8 w-64" />
      <div className="mt-6 flex gap-10">
        <div className="hidden w-[272px] shrink-0 flex-col gap-4 xl:flex">
          {GUIDE_STEPS.map((step) => (
            <Skeleton key={step.key} className="h-10 w-full" />
          ))}
        </div>
        <div className="flex flex-1 flex-col gap-4">
          <Skeleton className="h-10 w-48" />
          <Skeleton className="h-40 w-full" />
          <Skeleton className="h-64 w-full" />
        </div>
      </div>
    </div>
  );
}

/** A listing that does not exist, or one this person cannot open, will not appear on a retry. */
function isMissingListing(error: unknown): boolean {
  const status = (error as { status?: number } | null)?.status;
  return status === 404 || status === 403;
}

function GuideLoadError({ error, onRetry }: { error: unknown; onRetry: () => void }) {
  const missing = isMissingListing(error);
  return (
    <div className="mx-auto w-full max-w-xl px-[var(--page-padding-x)] py-12" data-testid="listing-guide-error">
      <div role="alert" className="rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-sm">
        <p className="text-destructive" data-testid="listing-guide-error-message">
          {missing
            ? "This listing doesn't exist or you can't open it."
            : "We couldn't load this listing. Check your connection and try again."}
        </p>
        <div className="mt-2 flex flex-wrap gap-2">
          {!missing && (
            <Button type="button" variant="outline" data-testid="listing-guide-retry" onClick={onRetry}>
              Retry
            </Button>
          )}
          <Button asChild variant="ghost">
            <Link to="/leasing/listings" data-testid="listing-guide-error-back">
              Back to Listings
            </Link>
          </Button>
        </div>
      </div>
    </div>
  );
}

/** Steps 3 to 7 before the draft Listing exists: the step opens, and says what to do first. */
function NeedsListingNotice({ stepKey, detailsPath }: { stepKey: GuideStepKey; detailsPath: string }) {
  const label = guideStepLabel(stepKey);
  return (
    <div data-testid="guide-step-needs-listing" data-step={stepKey}>
      <h1 className="text-2xl font-semibold text-foreground" data-testid="guide-step-heading">
        {label}
      </h1>
      <div className="mt-4 rounded-lg border border-border bg-card p-5">
        <p className="text-base font-semibold text-foreground">Save listing details first.</p>
        <p className="mt-1 text-sm text-muted-foreground">
          {label} is set up on the listing, and the listing is created when you save its rent, dates and lease terms.
        </p>
        <Button asChild variant="outline" className="mt-4">
          <Link to={detailsPath} data-testid="guide-needs-listing-link">
            Go to Listing details
          </Link>
        </Button>
      </div>
    </div>
  );
}

/** Waits for the saved data, then opens the guide at the first incomplete step (BR-107). */
function GuideResume({ listingId, unitId }: { listingId?: string; unitId?: string }) {
  const detail = useQuery({
    queryKey: ['listing', listingId],
    queryFn: () => fetchListing(listingId!),
    enabled: !!listingId,
  });
  const listing = detail.data?.listing ?? null;
  const resolvedUnitId = listingId ? (listing?.unitId ?? '') : (unitId ?? '');
  const guide = useGuideState({ unitId: resolvedUnitId, listing });

  if (listingId && detail.error) {
    return <GuideLoadError error={detail.error} onRetry={() => void detail.refetch()} />;
  }
  if (guide.isLoading || (listingId && !listing)) return <GuideSkeleton />;
  if (listing && listing.status !== 'draft') return <Navigate to={`/leasing/listings/${listing.id}`} replace />;
  return <Navigate to={guidePathFor(guide.firstIncomplete, { listingId, unitId: resolvedUnitId })} replace />;
}

/** `/leasing/listings/:id/setup`, and a draft opened from the listing detail page. */
export function DraftGuideRedirect({ listingId }: { listingId: string }) {
  return <GuideResume listingId={listingId} />;
}

export function GuideResumePage() {
  const { id, unitId } = useParams<{ id: string; unitId: string }>();
  return <GuideResume listingId={id} unitId={unitId} />;
}

export function ListingGuidePage() {
  const { id, unitId: routeUnitId, step } = useParams<{ id: string; unitId: string; step: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const handleRef = useRef<GuideStepHandle>(null);
  const createdRef = useRef<Listing | null>(null);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [primaryLabelOverride, setPrimaryLabelOverride] = useState<string | null>(null);

  const detail = useQuery({
    queryKey: ['listing', id],
    queryFn: () => fetchListing(id!),
    enabled: !!id,
  });
  const listing = id ? (detail.data?.listing ?? null) : null;
  const canDecide = detail.data?.permissions.canDecide ?? false;
  const unitId = id ? (listing?.unitId ?? '') : (routeUnitId ?? '');

  const { data: unitsData } = useQuery({ queryKey: ['units'], queryFn: () => fetchUnits() });
  const unit = (unitsData?.units ?? []).find((u) => u.id === unitId);
  const unitName = unit ? (formatUnitLabel(unit.unitNumber) ?? unit.propertyName) : 'this unit';
  const unitLabel = unit ? [unit.propertyName, formatUnitLabel(unit.unitNumber)].filter(Boolean).join(' · ') : null;

  const stepKey: GuideStepKey | null = isGuideStepKey(step) ? step : null;
  const guide = useGuideState({ unitId, listing, currentStep: stepKey ?? undefined });

  // A fresh step starts clean.
  useEffect(() => {
    setDirty(false);
    setSaving(false);
    createdRef.current = null;
  }, [stepKey, id]);

  const stepPath = useCallback(
    (key: GuideStepKey) => guidePathFor(key, { listingId: listing?.id, unitId }),
    [listing?.id, unitId]
  );

  const save = useCallback(async (): Promise<boolean> => {
    const ok = (await handleRef.current?.save()) ?? true;
    if (ok && id) void queryClient.invalidateQueries({ queryKey: ['listing', id] });
    return ok;
  }, [id, queryClient]);

  if (id && detail.isLoading) return <GuideSkeleton />;
  if (id && (detail.error || !listing)) {
    return <GuideLoadError error={detail.error ?? { status: 404 }} onRetry={() => void detail.refetch()} />;
  }
  // The guide revises a draft; anything published lives on the listing page.
  if (listing && listing.status !== 'draft') {
    return <Navigate to={`/leasing/listings/${listing.id}`} replace />;
  }
  if (!stepKey || (stepKey === 'preview' && listing)) {
    const target = stepKey ? stepPath('preview') : listing ? `/leasing/listings/${listing.id}/setup` : `/leasing/listings/new/${unitId}`;
    return <Navigate to={target} replace />;
  }
  const needsListing = !listing && !STEPS_BEFORE_LISTING.includes(stepKey);

  const index = GUIDE_STEPS.findIndex((s) => s.key === stepKey);
  const nextKey = GUIDE_STEPS[index + 1]?.key;
  const isReview = stepKey === 'review';

  const onContinue = async () => {
    if (needsListing) {
      navigate(stepPath('details'));
      return;
    }
    if (!(await save())) return;
    const created = createdRef.current;
    if (created) {
      createdRef.current = null;
      navigate(`/leasing/listings/${created.id}/setup/${nextKey ?? 'review'}`, { replace: true });
    } else if (nextKey) {
      navigate(stepPath(nextKey));
    }
  };

  const stepProps = {
    unitId,
    listing,
    handleRef,
    onDirtyChange: setDirty,
    onSavingChange: setSaving,
    onPrimaryLabelChange: setPrimaryLabelOverride,
  };

  return (
    <GuideShell
      stepKey={stepKey}
      unitLabel={unitLabel}
      guide={guide}
      listing={listing}
      stepPath={stepPath}
      dirty={dirty}
      saving={saving}
      save={save}
      onContinue={() => void onContinue()}
      backTo={index > 0 ? stepPath(GUIDE_STEPS[index - 1]!.key) : undefined}
      primaryLabel={needsListing ? 'Go to Listing details' : isReview ? 'Continue to payment' : (primaryLabelOverride ?? 'Save and continue')}
      primaryTestId={
        needsListing
          ? 'guide-go-to-details'
          : isReview
            ? 'guide-continue-payment'
            : stepKey === 'agent'
              ? 'listing-agent-policy-save'
              : 'guide-save-continue'
      }
      onDiscardDraft={
        listing && canDecide
          ? async () => {
              await closeListing(listing.id, { notifyOpenLeads: false });
              void queryClient.invalidateQueries({ queryKey: ['listings'] });
              void queryClient.invalidateQueries({ queryKey: ['listing', listing.id] });
              navigate('/leasing/listings');
            }
          : undefined
      }
    >
      {(actionBar: ReactNode) => (
        <>
          {needsListing && <NeedsListingNotice stepKey={stepKey} detailsPath={stepPath('details')} />}
          {!needsListing && stepKey === 'marketing' && <MarketingStep key={stepKey} {...stepProps} />}
          {!needsListing && stepKey === 'details' && (
            <ListingDetailsStep
              key={stepKey}
              {...stepProps}
              onListingCreated={(created) => {
                createdRef.current = created;
                void queryClient.invalidateQueries({ queryKey: ['listings'] });
              }}
            />
          )}
          {listing && stepKey === 'showings' && <ShowingsStep key={stepKey} {...stepProps} />}
          {listing && stepKey === 'contact' && <ContactScreeningStep key={stepKey} {...stepProps} />}
          {listing && stepKey === 'agent' && <AgentStep key={stepKey} {...stepProps} />}
          {listing && stepKey === 'review' && (
            <ReviewStep
              key={stepKey}
              {...stepProps}
              guide={guide}
              stepPath={stepPath}
              unitName={unitName}
              unitLabel={unitLabel}
              actionBar={actionBar}
            />
          )}
          {!(listing && stepKey === 'review') && actionBar}
        </>
      )}
    </GuideShell>
  );
}
